import React, { useState, useEffect, useMemo } from 'react';
import TipGlass from '../components/TipGlass';
import { createPortal } from 'react-dom';
import styled, { useTheme } from 'styled-components'; // [FLUID-3 2026-10-12] keyframes no longer used (rptPopIn went)
import {
  fetchTeamPerformance,
  fetchTeamPerformanceMonths,
  fetchPmLeaderboard,
  TeamPerformanceData,
  TeamPerformanceRow,
  TeamTrendPoint,
  TeamFunnelData,
  PmLeaderboardData,
} from '../services/api';
import { useRealtime } from '../hooks/useRealtime'; // [REALTIME] 2026-06-23
import GlassSelect from '../components/GlassSelect';
import { effectiveCurrentMonth } from '../utils/effectiveMonth';
import Tooltip from '../components/Tooltip';
import { SkeletonBox, TableSkeleton, Reveal } from '../components/Skeleton';
import { LoadFailed, isForbidden } from '../components/LoadState'; // [LOAD-STATES 2026-09-29]
import { useAuth } from '../contexts/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile'; // [PHONE-LAYOUT 2026-10-23] the trend chart's phone width
import { toastError } from '../components/toastService';
import {
  lgTile, lgSquircle, lgSurface, lgButtonPrimary, lgButtonSecondary, lgControlBg, lgCardSheen, LG_PRESS, LgPressProps,
} from '../styles/liquidGlass'; // [LIQUID-GLASS 2026-11-02, user] the app's glass (was buttonPrimary / buttonSecondary)
import { fluid } from '../styles/motion'; // [FLUID-3 2026-10-12]
import { usePresence } from '../hooks/usePresence'; // [FLUID-3 2026-10-12]
import { fluidDialog, fluidScrim, fluidCascade } from '../components/Fluid'; // [FLUID-3 2026-10-12]
import { downloadPmPerformanceReport, fetchPmReportData, pmNamesInReport, PM_REPORT_COLUMNS, PmReportMonth } from '../utils/pmPerformanceReport';
import { layoutMax } from '../utils/appZoom'; // [APP-FIT 2026-10-26] laptop width rules follow the laid-out width


/* [PERF 2026-08-14] Built ONCE - `toLocaleDateString(locale, {options})` constructs a fresh
 * Intl.DateTimeFormat per call (measured 90us on V8 vs 1.5us cached). Output identical. */
const MONTH_LONG_YEAR_FMT = new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' });

const Container = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
  min-width: 0;
  background: ${(p) => p.theme.surface};
`;

const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: ${(p) => (p.theme.colorScheme === 'dark'
    ? 'linear-gradient(180deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.04) 100%)'
    : 'linear-gradient(180deg, rgba(241,245,249,0.85) 0%, rgba(229,232,231,0.72) 100%)')};
  /* No backdrop-filter — it flashed white during AppLayout's PageFade tab
     transition (Chromium glitch) and the blur did nothing over the solid card. */
  border-bottom: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(22,25,23,0.10)')};
  box-shadow: inset 0 1px 0 ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.55)')};
  flex-wrap: wrap;
  gap: 10px;

  /* [MOBILE 2026-07-07] Phones keep the title + month filter on ONE row (title
     left, filter right) instead of stacking them full-width — the base row layout
     is retained; we only stop it wrapping and tighten the padding. */
  @media (max-width: 768px) {
    flex-wrap: nowrap;
    padding: 10px 12px;
  }
`;

const PageTitle = styled.h1`
  font-size: 16px;
  font-weight: 700;
  color: ${(p) => p.theme.text};
  margin: 0;

  /* [MOBILE 2026-07-07] Share the toolbar row with the month filter: take the
     free space and wrap within it (long team titles like
     "Team - Shreyansh Performance") rather than pushing the filter off the edge. */
  @media (max-width: 768px) {
    flex: 1;
    min-width: 0;
    font-size: 15px;
    line-height: 1.25;
  }
`;

// [MOBILE 2026-07-07] Holds the month filter on the right of the toolbar; never
// shrinks, so on a phone the title (left) wraps into the remaining space instead
// of squashing the select. Transparent on desktop — layout is unchanged there.
const FilterSlot = styled.div`
  flex-shrink: 0;
`;

const Section = styled.section`
  padding: 20px;
`;

const SectionHeading = styled.h2`
  font-size: 14px;
  font-weight: 700;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  margin: 0 0 14px;
  display: flex;
  align-items: center;
  gap: 8px;
  &::before {
    content: '';
    width: 3px;
    height: 16px;
    background: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
    border-radius: 2px;
  }
`;

const KpiRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 14px;
  margin-bottom: 4px;
`;

const KpiCard = styled.div`
  position: relative;
  isolation: isolate;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 14px;
  /* [LIQUID-GLASS 2026-11-02] glass: a shine on the top half (its light top edge is below) */
  background: ${(p) => p.theme.colorScheme === 'dark'
    ? 'linear-gradient(180deg, rgba(255,255,255,0.07), rgba(255,255,255,0) 50%), linear-gradient(180deg, rgba(86, 86, 86, 0.42) 0%, rgba(54, 54, 54, 0.55) 100%)'
    : 'linear-gradient(180deg, #ffffff, rgba(255,255,255,0) 55%), rgba(255, 255, 255, 0.96)'};
  /* No backdrop-filter — it flashed white during AppLayout's PageFade tab
     transition (Chromium glitch) and the blur did nothing over the solid card. */
  border: 1px solid ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.16)' : 'rgba(22,25,23,0.10)'};
  border-radius: 14px;
  padding: 16px 18px;
  box-shadow:
    0 8px 24px ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.32)' : 'rgba(0,0,0,0.08)'},
    inset 0 1px 0 ${(p) => p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(255,255,255,0.60)'};

  /* [PM-PERF PHONE KPI 2026-09-23, user] Phones: label on top, number underneath. Side by side,
     the number ran out of the tile and over the label (e.g. "$221,889" past the edge). Left-aligned
     to match the funnel cells below, which already read label-then-amount. Desktop unchanged. */
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    gap: 6px;
  }
`;

const KpiLabel = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${(p) => p.theme.muted};
  white-space: nowrap;
`;

const KpiValue = styled.div`
  font-size: 22px;
  font-weight: 800;
  color: ${(p) => p.theme.text};
  white-space: nowrap;
`;

/* [LIQUID-GLASS 2026-11-02, user] "make the table same as before": the PM Performance tables keep their old frame (no glass) */
const TableWrapper = styled.div`
  border: 1px solid ${(p) => p.theme.line};
  border-radius: 8px;
  overflow: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  min-width: 1300px;

  /* Row-hover identifier — same subtle neutral tint as the shared DataTable.
     Painted on the cells (not the row) so it overlays any row background (e.g.
     the totals row's pill) instead of replacing it, and guarded by hover:hover
     so touch/hybrid devices never get a sticky tint. The html.pointer-active
     prefix (set by DataTable on the first real pointer move) keeps a hard
     refresh with the cursor parked on a row from painting — and sticking — the
     highlight on load. */
  & tbody tr > td {
    transition: background 120ms cubic-bezier(0.4, 0, 0.2, 1), border-color 320ms cubic-bezier(0.4, 0, 0.2, 1);
  }
  @media (hover: hover) {
    html.pointer-active & tbody tr:hover > td {
      background: ${(p) =>
        p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(22,25,23,0.04)'};
    }
  }
`;

const Th = styled.th`
  background: ${(p) => p.theme.pill};
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  padding: 10px 12px;
  text-align: right;
  border-bottom: 1px solid ${(p) => p.theme.line};
  white-space: nowrap;
  &:first-child { text-align: left; }

  @media (max-width: 768px) { font-size: 11px; } /* [PHONE-LAYOUT 2026-10-23] the smallest label size on a phone (was 10) */
`;

const Td = styled.td`
  padding: 8px 12px;
  text-align: right;
  color: ${(p) => p.theme.text};
  border-bottom: 1px solid ${(p) => p.theme.line};
  white-space: nowrap;
  &:first-child { text-align: left; font-weight: 600; }
`;

const TotalRow = styled.tr`
  background: ${(p) => p.theme.pill};
  td { font-weight: 700; border-bottom: none; }
`;

// Leaderboard variant: every column centred (header + value), overriding the
// default first-child left-align. RankBadge / In-Bank-vs-Inventory bar centre too.
const LeaderTable = styled(Table)`
  th, td, th:first-child, td:first-child { text-align: center; }
`;

// Performance variant: every column centred except the first (Name), which
// keeps the default left-align.
const PerfTable = styled(Table)`
  th, td { text-align: center; }
  th:first-child, td:first-child { text-align: left; }
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 20px;
  color: ${(p) => p.theme.muted};
  font-size: 14px;
`;

function fmtMoney(n: number): string {
  const v = Math.round(n || 0);
  return '$' + v.toLocaleString('en-US');
}

function fmtPct(n: number | null): string {
  if (n == null) return '—';
  return (n || 0).toFixed(2) + '%';
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function formatMonthLabel(ym: string): string {
  const [y, m] = ym.split('-');
  return `${MONTH_NAMES[parseInt(m, 10) - 1]} ${y}`;
}

// [MONTH-FULL-NAME] 2026-06-23 — the month *dropdown* shows the full month name
// (e.g. "June 2026") to match the EOM page. The short form (formatMonthLabel) is
// kept for the SVG chart tooltip and section heading where width matters.
function formatMonthFull(ym: string): string {
  const [y, m] = ym.split('-');
  return MONTH_LONG_YEAR_FMT.format(new Date(parseInt(y, 10), parseInt(m, 10) - 1));
}

// [EFFECTIVE-MONTH 2026-07-01] App-wide effective current month (12:30 PM IST
// rollover on the 1st) so the default month + section-switch reset line up with the
// rest of the app instead of flipping at local midnight.
function getCurrentYm(): string {
  return effectiveCurrentMonth();
}

function shortMonth(ym: string): string {
  return MONTH_NAMES[parseInt(ym.split('-')[1], 10) - 1];
}

// Axis tick like "Jul '25" — month + two-digit year, so a 12-month span reads clearly.
function shortMonthYear(ym: string): string {
  const [y, m] = ym.split('-');
  return `${MONTH_NAMES[parseInt(m, 10) - 1]} '${y.slice(2)}`;
}

// Compact money for on-chart data labels: $980, $12.3k, $145k, $1.2M.
function fmtMoneyShort(n: number): string {
  const v = Math.round(n || 0);
  const a = Math.abs(v);
  if (a >= 1_000_000) return '$' + (v / 1_000_000).toFixed(1).replace(/\.0$/, '') + 'M';
  if (a >= 1_000) {
    const k = v / 1_000;
    return '$' + (a >= 100_000 ? Math.round(k).toString() : k.toFixed(1).replace(/\.0$/, '')) + 'k';
  }
  return '$' + v;
}

/* ── Glass panel + chart pieces for the below-table analytics ── */

const Panel = styled.div`
  ${lgSquircle(14)}
  ${lgTile} /* [LIQUID-GLASS 2026-11-02] a glass card; the chart inside stays as it is */
  padding: 18px;
  overflow-x: auto;
`;

// Heading rendered INSIDE a Panel, anchored to the top-left corner with a hairline
// divider so the title reads as part of the card (vs. floating above the box).
const PanelHeading = styled.h3`
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.01em;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  margin: 0 0 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(22,25,23,0.07)')};
  display: flex;
  align-items: center;
  gap: 8px;
  &::before {
    content: '';
    width: 3px;
    height: 14px;
    background: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
    border-radius: 2px;
  }
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 12px;
  font-size: 11px;
  color: ${(p) => p.theme.muted};
`;
const LegendItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;
/* [GRAPHITE-EMERALD 2026-09-28] Series colours are defined once (light); in dark the jade green and the
   sage grey swap to their Graphite & Emerald twins at the same lightness. Anything else passes through. */
const DARK_SERIES: Record<string, string> = { '#13b790': '#44b57b', '#6f7472': '#737373' };
const seriesColor = (c: string, dark: boolean) => (dark ? DARK_SERIES[c] ?? c : c);

const Swatch = styled.span<{ $c: string; $line?: boolean }>`
  width: ${(p) => (p.$line ? '14px' : '10px')};
  height: ${(p) => (p.$line ? '3px' : '10px')};
  border-radius: ${(p) => (p.$line ? '2px' : '3px')};
  background: ${(p) => seriesColor(p.$c, p.theme.colorScheme === 'dark')};
  display: inline-block;
`;

const FunnelBar = styled.div`
  display: flex;
  width: 100%;
  height: 30px;
  border-radius: 7px;
  overflow: hidden;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(22,25,23,0.05)')};
`;
const FunnelSeg = styled.div<{ $c: string }>`
  background: ${(p) => seriesColor(p.$c, p.theme.colorScheme === 'dark')};
  height: 100%;
`;
const FunnelGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 10px;
  margin-top: 16px;
`;
const FunnelCell = styled.div`
  border: 1px solid ${(p) => p.theme.line};
  border-radius: 10px;
  padding: 10px 12px;
  .lbl { display: flex; align-items: center; gap: 6px; font-size: 11px; color: ${(p) => p.theme.muted}; margin-bottom: 4px; }
  .amt { font-size: 15px; font-weight: 700; color: ${(p) => p.theme.text}; }
  .pct { font-size: 11px; color: ${(p) => p.theme.muted}; margin-left: 6px; font-weight: 600; }
`;

const PmFunnelStack = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
  align-items: stretch;
  ${layoutMax(1280)} { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  ${layoutMax(820)} { grid-template-columns: minmax(0, 1fr); }
`;
const PmFunnelHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
  .name { font-size: 13px; font-weight: 700; color: ${(p) => p.theme.text}; }
  .inv { font-size: 12px; color: ${(p) => p.theme.muted}; }
  .inv strong { color: ${(p) => p.theme.text}; margin-left: 6px; font-weight: 700; }
`;

const RankBadge = styled.span<{ $top?: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  font-size: 11px;
  font-weight: 800;
  color: ${(p) => (p.$top ? '#161917' : p.theme.text)};
  background: ${(p) => (p.$top ? '#f5c542' : p.theme.pill)}; /* [LIQUID-GLASS 2026-11-02, user] "make this same as before": no gloss */
`;

const EmptyNote = styled.div`
  padding: 18px;
  text-align: center;
  color: ${(p) => p.theme.muted};
  font-size: 13px;
`;

// Status → colour, in funnel order. Fixed hex reads fine in light + dark.
const STATUS_PARTS: { key: keyof TeamFunnelData; label: string; color: string }[] = [
  { key: 'retained', label: 'Retained', color: '#13b790' },
  { key: 'expected', label: 'Expected', color: '#6f7472' },
  { key: 'atRisk', label: 'At-Risk', color: '#f59e0b' },
  { key: 'atRiskExpected', label: 'At-Risk Expected', color: '#fb923c' },
  { key: 'cancelled', label: 'Cancelled', color: '#ef4444' },
];

// Legend cards below the bar use a different display order than the bar segments.
const STATUS_CARDS = (['expected', 'atRisk', 'atRiskExpected', 'retained', 'cancelled'] as const)
  .map((k) => STATUS_PARTS.find((s) => s.key === k)!);

const LINE_COLOR = '#3eafb3';

/** Bar + status-cell grid for one funnel — shared by the team total and each PM. */
const FunnelView: React.FC<{ f: TeamFunnelData }> = ({ f }) => (
  <>
    <FunnelBar>
      {STATUS_PARTS.map((s) => {
        const v = f[s.key] as number;
        const pct = f.initialExpected > 0 ? (v / f.initialExpected) * 100 : 0;
        return pct > 0
          ? <FunnelSeg key={s.key} $c={s.color} style={{ width: `${pct}%` }} title={`${s.label}: ${fmtMoney(v)} (${pct.toFixed(1)}%)`} />
          : null;
      })}
    </FunnelBar>
    <FunnelGrid>
      {STATUS_CARDS.map((s) => {
        const v = f[s.key] as number;
        const pct = f.initialExpected > 0 ? (v / f.initialExpected) * 100 : 0;
        return (
          <FunnelCell key={s.key}>
            <div className="lbl"><Swatch $c={s.color} />{s.label}</div>
            <div><span className="amt">{fmtMoney(v)}</span><span className="pct">{pct.toFixed(1)}%</span></div>
          </FunnelCell>
        );
      })}
    </FunnelGrid>
  </>
);

/* ── Loading skeleton ──
   Mirrors the REAL report layout 1:1 by reusing the same structural components
   (Section / KpiRow / KpiCard / Panel / FunnelBar+Grid / table / PmFunnelStack)
   with shimmer placeholders in the content slots — so the page doesn't reshape
   when data lands (no tall-card or generic-block mismatch). */

const PERF_SKELETON_COLS = [
  'Name', 'Placed Inventory', 'Retained', 'Expected', 'At-Risk', 'At-Risk Expected',
  'Cancelled', 'Current Retention', 'Expected Retention', 'Recurring Upsell',
  'One-Time Upsell', 'Total Upsell', 'Refund/Dispute', 'Dispute Won', // [PM-PERF-RD 2026-08-25]
].map((label) => ({ label }));

/** FunnelView in placeholder form: the bar track + the 5 status cells. */
const FunnelViewSkeleton: React.FC = () => (
  <>
    <FunnelBar><SkeletonBox $w="100%" $h="30px" $radius="0" /></FunnelBar>
    <FunnelGrid>
      {Array.from({ length: 5 }).map((_, i) => (
        <FunnelCell key={i}>
          <div className="lbl"><SkeletonBox $w="65%" $h="9px" /></div>
          <SkeletonBox $w="52%" $h="14px" />
        </FunnelCell>
      ))}
    </FunnelGrid>
  </>
);

/** Full-page loading placeholder for the Team Performance report. */
const PerfSkeleton: React.FC = () => (
  <>
    <Section>
      <KpiRow>
        {Array.from({ length: 4 }).map((_, i) => (
          <KpiCard key={i}>
            <SkeletonBox $w="55%" $h="11px" />
            <SkeletonBox $w="64px" $h="20px" />
          </KpiCard>
        ))}
      </KpiRow>
    </Section>

    <Section>
      <Panel>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 12 }}>
          <SkeletonBox $w="260px" $h="13px" />
          <SkeletonBox $w="80px" $h="15px" />
        </div>
        <FunnelViewSkeleton />
      </Panel>
    </Section>

    <Section>
      <SectionHeading><SkeletonBox $w="150px" $h="13px" /></SectionHeading>
      <TableWrapper>
        <TableSkeleton columns={PERF_SKELETON_COLS} rows={3} />
      </TableWrapper>
    </Section>

    <Section>
      <SectionHeading><SkeletonBox $w="230px" $h="13px" /></SectionHeading>
      <PmFunnelStack>
        {Array.from({ length: 3 }).map((_, i) => (
          <Panel key={i}>
            <PmFunnelHead>
              <SkeletonBox $w="120px" $h="13px" />
              <SkeletonBox $w="90px" $h="12px" />
            </PmFunnelHead>
            <FunnelViewSkeleton />
          </Panel>
        ))}
      </PmFunnelStack>
    </Section>
  </>
);

/** One PM's recurring inventory as a funnel. `cancelled` is the real sum of
 *  Cancelled-status inventory (server-computed), NOT a residual — a residual
 *  silently absorbed any row whose status didn't match the other four buckets
 *  (e.g. the "At-Risk"/"At - Risk" spelling split), overstating Cancelled. */
function rowToFunnel(r: TeamPerformanceRow): TeamFunnelData {
  return {
    initialExpected: r.effectiveInventory,
    retained: r.retained,
    expected: r.expected,
    atRisk: r.atRisk,
    atRiskExpected: r.atRiskExpected,
    cancelled: r.cancelled,
    placedInBank: r.placedInBank,
  };
}

/* ── PM Leaderboard: nested "In-Bank vs Inventory" bar ──
   The full-colour green bar (the brand green, same family as the trend-chart bars)
   is Placed Inventory (the "main" bar). The champagne-gold bar is Placed In Bank
   ("money landed"), growing left→right inside it. While In-Bank ≤ Inventory the gold
   sits within the green frame (the green tail on the right is the uncollected gap).
   When In-Bank exceeds Inventory the gold bursts past the green bar's right wall: the
   frame keeps its top/left/bottom edge (and a wall marker shows where it broke), while
   the gold overflows to the right — so over-collection reads at a glance. Both colours
   adapt to the light/dark theme (the gold bar carries a hairline ring so it stays
   legible on the green frame AND on the page background when it overflows).
   Palette = Emerald + Gold on Navy (premium "wealth" triad); gold replaced the old
   pure-white fill, which read raw on the dark UI. */
const LB_INV_DARK = '#0e7e4e'; // [GRAPHITE-EMERALD] emerald, = dark green600
const LB_INV_LIGHT = '#1d896d';
/* Deeper, more saturated gold in light mode so it stays legible on the green frame
   AND on the light grey page bg when it overflows (a pale champagne would wash out). */
const LB_BANK_DARK = '#e8c878';
const LB_BANK_LIGHT = '#c9962e';

const BarTrack = styled.div`
  position: relative;
  width: 210px;
  height: 24px;
  margin: 0 auto;
`;
const InvBar = styled.div<{ $w: number; $broken: boolean }>`
  position: absolute;
  left: 0;
  top: 0;
  height: 100%;
  width: ${(p) => p.$w}%;
  min-width: ${(p) => (p.$w > 0 ? '3px' : '0')};
  background: ${(p) => (p.theme.colorScheme === 'dark' ? LB_INV_DARK : LB_INV_LIGHT)};
  border-radius: 6px;
  ${(p) => p.$broken && 'border-top-right-radius: 0; border-bottom-right-radius: 0;'}
`;
const BankBar = styled.div<{ $w: number }>`
  position: absolute;
  left: 0;
  top: 4px;
  height: calc(100% - 8px);
  width: ${(p) => p.$w}%;
  min-width: ${(p) => (p.$w > 0 ? '3px' : '0')};
  background: ${(p) => (p.theme.colorScheme === 'dark' ? LB_BANK_DARK : LB_BANK_LIGHT)};
  border-radius: 4px;
  /* Hairline ring so the gold bar reads on the green frame AND on the page
     background when it overflows (matters most in the light theme). */
  box-shadow: 0 0 0 1px ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(7,7,7,0.55)' : 'rgba(22,25,23,0.28)')};
  z-index: 2;
`;
const BarWall = styled.div`
  position: absolute;
  top: 0;
  height: 100%;
  width: 2px;
  border-radius: 1px;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? '#1f5f3f' : '#1e7b61')};
  z-index: 3;
`;

const BankVsInvBar: React.FC<{ inventory: number; bank: number; scale: number }> = ({ inventory, bank, scale }) => {
  const invW = scale > 0 ? Math.max(0, (inventory / scale) * 100) : 0;
  const bankW = scale > 0 ? Math.max(0, (bank / scale) * 100) : 0;
  const broken = bank > inventory && bank > 0;
  const pct = inventory > 0 ? (bank / inventory) * 100 : 0;
  const label = `In Bank ${fmtMoney(bank)} of ${fmtMoney(inventory)} placed${inventory > 0 ? ` (${pct.toFixed(0)}%)` : ''}`;
  return (
    <Tooltip label={label} placement="bottom">
      <BarTrack>
        {inventory > 0 && <InvBar $w={invW} $broken={broken} />}
        {bank > 0 && <BankBar $w={bankW} />}
        {broken && inventory > 0 && <BarWall style={{ left: `calc(${invW}% - 1px)` }} />}
      </BarTrack>
    </Tooltip>
  );
};

const LB_MONTH_DASH = ' – ';
/** Heading label for the leaderboard date window: "Jun 1 – Jun 19, 2026"
 *  (adds the start year too when the window spans a year boundary). */
function leaderboardWindowLabel(start: string, end: string): string {
  if (!start || !end) return '';
  const [sy, sm, sd] = start.split('-').map(Number);
  const [ey, em, ed] = end.split('-').map(Number);
  const left = `${MONTH_NAMES[sm - 1]} ${sd}${sy === ey ? '' : `, ${sy}`}`;
  const right = `${MONTH_NAMES[em - 1]} ${ed}, ${ey}`;
  return `${left}${LB_MONTH_DASH}${right}`;
}

/** Month-over-month combo chart: inventory bars (context) + retention % line (story). */
const TrendChart: React.FC<{ points: TeamTrendPoint[] }> = ({ points }) => {
  const theme = useTheme() as any;
  const dark = theme.colorScheme === 'dark';
  const [hover, setHover] = useState<number | null>(null);
  /* [PHONE-LAYOUT 2026-10-23] Phones: squeezed to its 640 px floor the 820-unit drawing put its labels at 6.6-7 px; there it
     is drawn 1,100 px wide (it swipes sideways in its box), so a value label (8.5 units) is about 11.4 px. */
  const isMobile = useIsMobile();
  const W = 820, H = 300, ml = 56, mr = 52, mt = 40, mb = 38;
  const pw = W - ml - mr, ph = H - mt - mb;
  const n = Math.max(points.length, 1);
  const maxInv = Math.max(...points.map((p) => p.inventory), 1);
  const maxRet = Math.max(...points.map((p) => p.retentionPct), 1) * 1.25;
  const band = pw / n;
  const barW = Math.min(band * 0.55, 34);
  const cx = (i: number) => ml + band * i + band / 2;
  const yInv = (v: number) => mt + ph - (v / maxInv) * ph;
  const yRet = (v: number) => mt + ph - (v / maxRet) * ph;
  const axis = dark ? 'rgba(255,255,255,0.55)' : 'rgba(22,25,23,0.5)';
  // On-theme brand-green bars (was blue in dark) + readable label inks.
  const barFill = dark ? 'rgba(14,126,78,0.42)' : 'rgba(20,107,84,0.16)';
  // Inner bar = Placed In Bank (collected); teal so the gap above it (uncollected) reads at a glance.
  const inBankFill = 'rgba(62,175,179,0.9)';
  const inBankW = Math.min(barW * 0.52, 18);
  const barLbl = dark ? 'rgba(231,231,231,0.82)' : 'rgba(22,25,23,0.6)';
  const retLbl = dark ? '#7fd6d9' : '#1c7c80';
  const line = points.map((p, i) => `${cx(i).toFixed(1)},${yRet(p.retentionPct).toFixed(1)}`).join(' ');

  // Persistent data labels: $ above each bar, % above each dot. When the dot sits
  // near a bar top the two would stack, so split them — % up, $ tucked below the top.
  const labelPos = points.map((p) => {
    const dotY = yRet(p.retentionPct);
    const barTopY = yInv(p.inventory);
    let retY = dotY - 9;
    let invY = barTopY - 6;
    if (Math.abs(dotY - barTopY) < 16) {
      retY = Math.min(dotY, barTopY) - 10;
      invY = Math.max(dotY, barTopY) + 13;
    }
    return { retY: Math.max(11, retY), invY };
  });

  // Tooltip geometry (in-SVG so it scales with the chart); clamped to bounds.
  const tipW = 132, tipH = 52;
  const hp = hover != null ? points[hover] : null;
  const tipX = hover != null ? Math.max(4, Math.min(W - tipW - 4, cx(hover) - tipW / 2)) : 0;
  const tipAnchorY = hp ? yRet(hp.retentionPct) : 0;
  const tipY = tipAnchorY - tipH - 12 < 4 ? tipAnchorY + 14 : tipAnchorY - tipH - 12;
  // [GLASS TOOLTIP 2026-08-31, user] The solid rect became the shared TipGlass capsule —
  // this was the last solid chart tooltip in the app (every HTML tooltip was already glass).
  const tipText = dark ? '#e5e7eb' : '#161917';
  const tipMuted = dark ? 'rgba(229,231,235,0.65)' : 'rgba(22,25,23,0.55)';

  return (
    // [RESPONSIVE 2026-07-07] Scroll-wrap the fixed-min-width chart so on a narrow
    // card the 640px SVG scrolls inside this box instead of overflowing its Section
    // and giving the whole page card a horizontal scrollbar.
    <div style={{ overflowX: 'auto', overflowY: 'hidden' }}>
    <svg
      viewBox={`0 0 ${W} ${H}`} width="100%" preserveAspectRatio="xMidYMid meet"
      style={{ display: 'block', minWidth: isMobile ? 1100 : 640 /* [PHONE-LAYOUT 2026-10-23] */ }}
      onMouseLeave={() => setHover(null)}
    >
      {/* baseline */}
      <line x1={ml} y1={mt + ph} x2={W - mr} y2={mt + ph} stroke={axis} strokeWidth={0.75} />
      {/* hover guide line */}
      {hover != null && (
        <line x1={cx(hover)} y1={mt} x2={cx(hover)} y2={mt + ph} stroke={axis} strokeWidth={0.75} strokeDasharray="3 3" opacity={0.5} />
      )}
      {/* bars: outer = Placed Inventory (green), inner = Placed In Bank (teal),
          so the uncollected gap is visible without the tooltip. Dim when hovering elsewhere. */}
      {points.map((p, i) => (
        // [PREMIUM HOVER 2026-08-31, user] Harmonised with the Process Analysis charts:
        // subtle 0.72 dim (was a heavy 0.4) + brighten on the hovered column. No lift -
        // the retention line pins a dot to every column, same rule as the money-flow chart.
        <g key={`b${i}`} style={{ transition: 'opacity 160ms cubic-bezier(0.4, 0, 0.2, 1), filter 160ms cubic-bezier(0.4, 0, 0.2, 1)', opacity: hover == null || hover === i ? 1 : 0.72, filter: hover === i ? 'brightness(1.08)' : 'none' }}>
          <rect
            x={cx(i) - barW / 2} y={yInv(p.inventory)} width={barW} height={mt + ph - yInv(p.inventory)}
            fill={barFill} rx={2}
          />
          <rect
            x={cx(i) - inBankW / 2} y={yInv(p.placed)} width={inBankW} height={mt + ph - yInv(p.placed)}
            fill={inBankFill} rx={2}
          />
        </g>
      ))}
      {/* inventory ($) value labels above each bar */}
      {points.map((p, i) => (
        <text
          key={`bl${i}`} x={cx(i)} y={labelPos[i].invY} fontSize="8.5" fill={barLbl}
          textAnchor="middle" fontWeight={600}
          style={{ transition: 'opacity 160ms cubic-bezier(0.4, 0, 0.2, 1), filter 160ms cubic-bezier(0.4, 0, 0.2, 1)', opacity: hover == null || hover === i ? 1 : 0.72, filter: hover === i ? 'brightness(1.08)' : 'none' }}
        >{fmtMoneyShort(p.inventory)}</text>
      ))}
      {/* retention line + dots */}
      <polyline points={line} fill="none" stroke={LINE_COLOR} strokeWidth={2} strokeLinejoin="round" />
      {points.map((p, i) => (
        <circle
          key={`d${i}`} cx={cx(i)} cy={yRet(p.retentionPct)} fill={LINE_COLOR}
          r={hover === i ? 4.5 : 2.6} style={{ transition: 'r 120ms cubic-bezier(0.4, 0, 0.2, 1)' }}
        />
      ))}
      {/* retention (%) value labels above each dot */}
      {points.map((p, i) => (
        <text
          key={`rl${i}`} x={cx(i)} y={labelPos[i].retY} fontSize="9" fill={retLbl}
          textAnchor="middle" fontWeight={700}
        >{p.retentionPct.toFixed(0)}%</text>
      ))}
      {/* x labels — month + year, e.g. Jul '25 */}
      {points.map((p, i) => (
        <text key={`x${i}`} x={cx(i)} y={H - 9} fontSize="9" fill={axis} textAnchor="middle" fontWeight={hover === i ? 700 : 400}>{shortMonthYear(p.month)}</text>
      ))}
      {/* left $ axis */}
      <text x={ml - 8} y={mt + ph} fontSize="9" fill={axis} textAnchor="end">$0</text>
      {/* right % axis */}
      <text x={W - mr + 8} y={mt + ph} fontSize="9" fill={LINE_COLOR} textAnchor="start">0%</text>

      {/* tooltip (glides between columns; fades in/out) */}
      <g
        transform={`translate(${tipX}, ${tipY})`} pointerEvents="none"
        style={{ transition: 'transform 120ms cubic-bezier(0.4, 0, 0.2, 1), opacity 120ms cubic-bezier(0.4, 0, 0.2, 1)', opacity: hp ? 1 : 0 }}
      >
        <TipGlass w={tipW} h={tipH} dark={dark} />
        {hp && (
          <>
            <text x={8} y={13} fontSize="7.5" fontWeight={700} fill={tipText}>{formatMonthLabel(hp.month)}</text>
            <text x={8} y={26} fontSize="7" fill={tipMuted}>Inventory: <tspan fill={tipText} fontWeight={600}>{fmtMoney(hp.inventory)}</tspan></text>
            <text x={8} y={37} fontSize="7" fill={tipMuted}>In-Bank: <tspan fill={tipText} fontWeight={600}>{fmtMoney(hp.placed)}</tspan></text>
            <text x={8} y={48} fontSize="7" fill={tipMuted}>Retention %: <tspan fill={LINE_COLOR} fontWeight={700}>{hp.retentionPct.toFixed(2)}%</tspan></text>
          </>
        )}
      </g>

      {/* transparent hit areas (full-height per column) — on top to capture hover */}
      {points.map((p, i) => (
        <rect
          key={`h${i}`} x={ml + band * i} y={mt} width={band} height={ph}
          fill="transparent" onMouseEnter={() => setHover(i)}
        />
      ))}
    </svg>
    </div>
  );
};

/* [PM-PERF-REPORT 2026-07-13] Glass download button + month-picker modal — matches the
   app's premium modal language (AddPaymentModal): blurred backdrop, frosted gradient panel,
   glassy controls. Rendered via a portal so it escapes the scrolling PageCard.
   [FLUID-3 2026-10-12, user] Opens and closes with the fluid motion (components/Fluid.tsx): the shade fades in and
   out, the panel rises 12px and grows from 0.95 on the spring and closes in 0.18 s, its blocks rising in one
   after another. It used to pop in (rptFade / rptPopIn) and vanish on close; it is now held mounted for the close. */
// [BUTTON-STYLE 2026-09-30] The green primary from styles/buttons.
const DownloadReportBtn = styled.button.attrs<LgPressProps>(LG_PRESS)`
  ${lgButtonPrimary} /* [LIQUID-GLASS 2026-11-02] the glossy green capsule, at the old 32 px */
  height: 32px;
  padding: 0 14px;
`;
const ReportOverlay = styled.div<{ $open: boolean }>`
  position: fixed; inset: 0; z-index: 1200;
  display: flex; align-items: center; justify-content: center; padding: 20px;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.35)' : 'rgba(20,30,25,0.18)')};
  backdrop-filter: blur(12px) saturate(115%); /* [LIQUID-GLASS 2026-11-02] Edit Mapping's shade */
  -webkit-backdrop-filter: blur(12px) saturate(115%);
  ${(p) => fluidScrim(p.$open)}
`;
const ReportPanel = styled.div<{ $open: boolean }>`
  ${(p) => fluidDialog({ open: p.$open })}
  ${(p) => (p.$open ? fluidCascade('> *', 0, 20, 5) /* [FLUID-8 2026-10-12] soft: readable at once */ : '')}
  position: relative; width: 100%; max-width: 720px; max-height: calc(88vh / var(--app-zoom, 1)); /* [APP-FIT 2026-10-26] the window in page px */
  display: flex; flex-direction: column; padding: 22px;
  color: ${(p) => p.theme.text};
  /* [LIQUID-GLASS 2026-11-02, user] the app's frosted glass window (was the old blur 48 px, 180 % recipe) */
  ${lgSurface(38)}
  ${lgSquircle(22)}
`;
const ReportColsBar = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px; align-items: center;
  padding: 9px 11px; margin-bottom: 14px; border-radius: 12px;
  /* [LIQUID-GLASS 2026-11-02] pressed into the glass */
  border: 1px solid transparent;
  background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(0,0,0,0.30)' : 'rgba(255,255,255,0.66)')};
  box-shadow: ${(p) => (p.theme.colorScheme === 'dark' ? 'inset 0 1px 2px rgba(0,0,0,0.5), inset 0 -1px 0 rgba(255,255,255,0.07)' : 'inset 0 1px 2px rgba(22,25,23,0.14), inset 0 0 0 1px rgba(22,25,23,0.07)')};
`;
const ReportColChip = styled.span`
  font-size: 11px; font-weight: 600; padding: 3px 8px; border-radius: 7px;
  color: ${(p) => p.theme.muted};
  /* [LIQUID-GLASS 2026-11-02] a small glass chip (a shine, a light top edge) */
  background-color: ${lgControlBg};
  background-image: ${lgCardSheen};
  border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)')};
  box-shadow: inset 0 1px 0 ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.10)' : '#ffffff')};
`;
const ReportMonthList = styled.div`
  overflow-y: auto; display: grid; gap: 4px; max-height: 268px; padding-right: 3px;
  align-content: start;
`;
/* [FLUID-3 2026-10-12, user] One open year's month rows, grouped so they ripple in one after another when the
   year is expanded (and when the window opens on it) — like Inventory's month cells. The same 4px grid rhythm as
   the list around it, so the rows sit exactly where they did. */
const ReportYearMonths = styled.div`
  display: grid; gap: 4px;
  ${fluidCascade('> label', 0, 12)}
`;
const ReportGrid = styled.div`
  display: grid; grid-template-columns: 1fr 1fr; gap: 18px; margin-bottom: 18px;
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;
const ReportColHead = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  font-size: 12px; font-weight: 700; color: ${(p) => p.theme.text}; margin: 0 2px 9px;
`;
const ReportColLink = styled.button`
  background: transparent; border: none; padding: 0; cursor: pointer;
  font-size: 11.5px; font-weight: 700;
  color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : p.theme.green600)};
  &:hover { text-decoration: underline; }
`;
const ReportHint = styled.div`
  font-size: 12.5px; color: ${(p) => p.theme.muted}; padding: 20px 6px; text-align: center;
`;
const ReportMonthRow = styled.label<{ $on: boolean }>`
  display: flex; align-items: center; gap: 11px; padding: 9px 11px; border-radius: 11px;
  cursor: pointer; font-size: 13.5px; font-weight: 500;
  border: 1px solid ${(p) => (p.$on ? (p.theme.colorScheme === 'dark' ? 'rgba(74,205,139,0.38)' : 'rgba(19,183,144,0.32)') : 'transparent')};
  background: ${(p) => (p.$on ? (p.theme.colorScheme === 'dark' ? 'rgba(74,205,139,0.13)' : 'rgba(19,183,144,0.10)') : 'transparent')};
  /* [LIQUID-GLASS 2026-11-02] a picked month: a green glass highlight (a shine, a light top edge) */
  ${(p) => (p.$on ? `background-image: linear-gradient(180deg, rgba(255,255,255,${p.theme.colorScheme === 'dark' ? '0.08' : '0.7'}), rgba(255,255,255,0) 70%); box-shadow: inset 0 1px 0 rgba(255,255,255,${p.theme.colorScheme === 'dark' ? '0.12' : '0.85'});` : '')}
  transition: background 120ms cubic-bezier(0.4, 0, 0.2, 1), border-color 120ms cubic-bezier(0.4, 0, 0.2, 1);
  &:hover { background: ${(p) => (p.$on ? (p.theme.colorScheme === 'dark' ? 'rgba(74,205,139,0.18)' : 'rgba(19,183,144,0.14)') : (p.theme.colorScheme === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'))}; }
  input { width: 16px; height: 16px; accent-color: ${(p) => (p.theme.colorScheme === 'dark' ? '#44b57b' : '#13b790')}; cursor: pointer; flex-shrink: 0; }
`;
/* [YEAR-COLLAPSE 2026-07-30] Sticky-feeling year header inside the month list: chevron +
   year + "n of 12", with a per-year All/Clear on the right. */
const ReportYearRow = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 8px;
  padding: 8px 4px 6px;
  & + & { margin-top: 2px; }
  .yr {
    display: inline-flex; align-items: center; gap: 8px; flex: 1; min-width: 0;
    background: none; border: none; cursor: pointer; text-align: left;
    font-size: 11px; font-weight: 800; letter-spacing: 0.6px;
    color: ${(p) => p.theme.muted};
    i { font-size: 9px; width: 9px; flex: none; }
    .cnt { font-size: 10px; font-weight: 600; opacity: 0.75; letter-spacing: 0; }
    &:hover { color: ${(p) => p.theme.text}; }
  }
  .all {
    flex: none; padding: 3px 9px; border-radius: 999px;
    font-size: 10px; font-weight: 700; cursor: pointer;
    color: ${(p) => (p.theme.colorScheme === 'dark' ? '#4acd8b' : '#1c9475')};
    background: ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(74,205,139,0.12)' : 'rgba(19,183,144,0.10)')};
    border: 1px solid ${(p) => (p.theme.colorScheme === 'dark' ? 'rgba(74,205,139,0.28)' : 'rgba(19,183,144,0.24)')};
    background-image: linear-gradient(180deg, rgba(255,255,255,${(p) => (p.theme.colorScheme === 'dark' ? '0.10' : '0.7')}), rgba(255,255,255,0) 65%); /* [LIQUID-GLASS] glossy */
    box-shadow: inset 0 1px 0 rgba(255,255,255,${(p) => (p.theme.colorScheme === 'dark' ? '0.14' : '0.85')});
    &:hover { filter: brightness(1.1); }
  }
`;
const ReportActions = styled.div` display: flex; justify-content: flex-end; gap: 10px; `;
// [BUTTON-STYLE 2026-09-30] $primary = the green primary, otherwise the secondary (Cancel).
const GlassBtn = styled.button.attrs<LgPressProps>(LG_PRESS)<{ $primary?: boolean }>`
  ${(p) => (p.$primary ? lgButtonPrimary : lgButtonSecondary)} /* [LIQUID-GLASS 2026-11-02] the glass capsules */
`;

interface TeamPerformancePageProps {
  teamKey: string;
  title: string;
  /** [PMPERF-SLIDE 2026-10-08] Passed by PerformancePage ONLY while this team loads hidden, waiting to slide
   *  in: called once its figures and leaderboard (or its error card) are drawn. */
  onReady?: () => void;
}

const TeamPerformancePage: React.FC<TeamPerformancePageProps> = ({ teamKey, title, onReady }) => {
  const theme = useTheme() as any;
  const accentGreen = theme.colorScheme === 'dark' ? '#4acd8b' : theme.green600;
  const [months, setMonths] = useState<string[]>([]);
  const [month, setMonth] = useState<string>(getCurrentYm());
  const [data, setData] = useState<TeamPerformanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // [LOAD-STATES 2026-09-29, user] Retry counter for the countdown card + whether the error is a final 403.
  const [reloadN, setReloadN] = useState(0);
  const [forbidden, setForbidden] = useState(false);
  // PM Leaderboard is fetched separately and keyed ONLY on the team — never the
  // month — so the toolbar month filter cannot move it. It always shows the
  // rolling cumulative window the backend computes.
  const [leaderboard, setLeaderboard] = useState<PmLeaderboardData | null>(null);

  // [SECTION-MONTH-INDEPENDENT] 2026-07-03 — Each Performance section (the
  // "Overall" tab and every team tab) filters its month INDEPENDENTLY. The parent
  // (PerformancePage) deliberately reuses ONE instance of this component across
  // tabs, so month/data/error state would otherwise persist between sections and a
  // month chosen under "Overall" would leak into a team tab. When the section
  // (teamKey) changes we reset — during render, per React's documented "adjust
  // state when a prop changes" pattern — the month back to the current month and
  // clear the report so the skeleton (not the previous team's dimmed data) shows
  // while the new section loads. A month change WITHIN the same section leaves
  // teamKey untouched, so this block doesn't fire and the report stays mounted
  // (dim) instead of flashing the skeleton. Resetting in render re-renders before
  // commit, so the data effect below still fires exactly once with the correct
  // month — no wasted refetch and no stale-month flash.
  const [prevTeamKey, setPrevTeamKey] = useState(teamKey);
  if (teamKey !== prevTeamKey) {
    setPrevTeamKey(teamKey);
    setMonth(getCurrentYm());
    setData(null);
    setError(null);
  }

  // [PM-PERF-REPORT 2026-07-13] Download-report feature — Overall section only, and only
  // for admins or SENIOR staff (the User-Management "Senior" toggle → is_senior).
  const { user } = useAuth();
  const canDownloadReport = teamKey === 'overall' && (!!user?.is_admin || !!user?.is_senior);
  const [reportOpen, setReportOpen] = useState(false);
  const reportPres = usePresence(reportOpen, fluid.dialogOutMs); // [FLUID-3 2026-10-12] held for its 0.18 s close
  const [reportMonths, setReportMonths] = useState<string[]>([]);
  const [downloadingReport, setDownloadingReport] = useState(false);
  // PM step: data for the chosen months → the PMs present → which the user picks.
  const [reportData, setReportData] = useState<PmReportMonth[] | null>(null);
  const [availablePms, setAvailablePms] = useState<string[]>([]);
  const [reportPms, setReportPms] = useState<string[]>([]);
  const [loadingPms, setLoadingPms] = useState(false);
  // [ALL-MONTHS 2026-07-30] The toolbar's list is floored at Jan-2026 (MIN_DROPDOWN_MONTH),
  // so the report could only ever cover 2026 even though inventory holds 29 months back to
  // Apr-2024. The report picker fetches the UNFLOORED list instead. Seeded from `months` so
  // it is never briefly empty; the full list replaces it when it lands. Only fetched once
  // the user can actually see the button.
  const [allReportMonths, setAllReportMonths] = useState<string[]>([]);
  useEffect(() => {
    if (!canDownloadReport) return;
    let alive = true;
    fetchTeamPerformanceMonths(true)
      .then((ms) => { if (alive && ms.length) setAllReportMonths(ms); })
      .catch(() => { /* fall back to the floored list below */ });
    return () => { alive = false; };
  }, [canDownloadReport]);
  const reportMonthOptions = useMemo(() => {
    const cur = getCurrentYm();
    const base = allReportMonths.length ? allReportMonths : months;
    const set = base.includes(cur) ? base : [cur, ...base];
    return [...set].sort().reverse();
  }, [months, allReportMonths]);

  // [YEAR-COLLAPSE 2026-07-30] Newest year first; months newest-first within each year.
  const reportMonthGroups = useMemo(() => {
    const byYear = new Map<string, string[]>();
    for (const m of reportMonthOptions) {
      const y = m.slice(0, 4);
      if (!byYear.has(y)) byYear.set(y, []);
      byYear.get(y)!.push(m);
    }
    return [...byYear.entries()]
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .map(([year, ms]) => ({ year, months: ms }));
  }, [reportMonthOptions]);
  const [openReportYears, setOpenReportYears] = useState<string[]>([]);
  const toggleReportYear = (y: string) =>
    setOpenReportYears((prev) => (prev.includes(y) ? prev.filter((x) => x !== y) : [...prev, y]));
  // Open the newest year (and any year holding a pick) whenever the modal opens or the
  // month list arrives — without fighting a user who has since collapsed something.
  useEffect(() => {
    if (!reportOpen) return;
    const withSel = new Set(reportMonths.map((m) => m.slice(0, 4)));
    const newest = reportMonthGroups[0]?.year;
    setOpenReportYears((prev) => {
      const want = reportMonthGroups.filter((g) => g.year === newest || withSel.has(g.year)).map((g) => g.year);
      const next = [...new Set([...prev, ...want])];
      return next.length === prev.length ? prev : next;
    });
  }, [reportOpen, reportMonthGroups]);
  const openReport = () => {
    setReportMonths([getCurrentYm()]);
    setReportData(null); setAvailablePms([]); setReportPms([]);
    setReportOpen(true);
  };
  const toggleReportMonth = (m: string) =>
    setReportMonths((prev) => (prev.includes(m) ? prev.filter((x) => x !== m) : [...prev, m]));
  const toggleReportPm = (pm: string) =>
    setReportPms((prev) => (prev.includes(pm) ? prev.filter((x) => x !== pm) : [...prev, pm]));
  const allPmsSelected = availablePms.length > 0 && reportPms.length === availablePms.length;
  const handleDownloadReport = () => {
    if (!reportData || !reportMonths.length || !reportPms.length) return;
    setDownloadingReport(true);
    try {
      downloadPmPerformanceReport(reportData, reportMonths, reportPms);
      setReportOpen(false);
    } catch (e) {
      console.error('[PM report] failed:', e);
      toastError('Could not generate the report. Please try again.');
    } finally {
      setDownloadingReport(false);
    }
  };
  // Lock the page's scroll while the modal is open. The app scrolls inside PageCard
  // (data-page-scroll), not the body, so we freeze that element (and the body as a
  // belt-and-suspenders) and restore it on close.
  useEffect(() => {
    if (!reportOpen) return;
    const scroller = document.querySelector('[data-page-scroll]') as HTMLElement | null;
    const prevScroller = scroller ? scroller.style.overflow : '';
    const prevBody = document.body.style.overflow;
    if (scroller) scroller.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';
    return () => {
      if (scroller) scroller.style.overflow = prevScroller;
      document.body.style.overflow = prevBody;
    };
  }, [reportOpen]);
  // Whenever the chosen months change, load the PMs present in that range so the user
  // can pick which to include. The fetched data is reused to build the PDF (no re-fetch).
  useEffect(() => {
    if (!reportOpen) return;
    if (reportMonths.length === 0) { setReportData(null); setAvailablePms([]); setReportPms([]); return; }
    let cancelled = false;
    setLoadingPms(true);
    fetchPmReportData(reportMonths)
      .then((d) => {
        if (cancelled) return;
        const pms = pmNamesInReport(d);
        setReportData(d);
        setAvailablePms(pms);
        setReportPms(pms); // default: all PMs selected
      })
      .catch((e) => { if (!cancelled) { console.error('[PM report] PM list failed:', e); toastError('Could not load PMs for the selected months.'); } })
      .finally(() => { if (!cancelled) setLoadingPms(false); });
    return () => { cancelled = true; };
  }, [reportOpen, reportMonths]);

  useEffect(() => {
    fetchTeamPerformanceMonths()
      .then(setMonths)
      .catch((err) => console.error('Failed to load months:', err));
  }, []);

  // [LOAD-STATES 2026-09-29, user] A failed leaderboard used to stay a skeleton until the next live
  // update. It now retries by itself every 10 s, and the page's "Try now" (reloadN) reloads it too.
  const [lbTry, setLbTry] = useState(0);
  useEffect(() => {
    let alive = true;
    let timer: number | undefined;
    setLeaderboard(null);
    fetchPmLeaderboard(teamKey)
      .then((d) => { if (alive) setLeaderboard(d); })
      .catch((err) => {
        console.error('Failed to load PM leaderboard:', err);
        if (alive) timer = window.setTimeout(() => setLbTry((n) => n + 1), 10000);
      });
    return () => { alive = false; if (timer) window.clearTimeout(timer); };
  }, [teamKey, lbTry, reloadN]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(null);
    fetchTeamPerformance(teamKey, month)
      .then((d) => { if (alive) setData(d); })
      .catch((err) => {
        console.error('Failed to load team performance:', err);
        if (alive) {
          setError(err?.response?.data?.error || err?.message || 'Failed to load data');
          setForbidden(isForbidden(err)); // [LOAD-STATES 2026-09-29]
        }
      })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [teamKey, month, reloadN]);

  // [REALTIME] 2026-06-23 — silently refresh the report + leaderboard for the
  // current team/month when the server signals inventory/payments changed.
  useRealtime(['teamPerformance'], () => {
    fetchTeamPerformance(teamKey, month).then(setData).catch(() => {});
    fetchPmLeaderboard(teamKey).then(setLeaderboard).catch(() => {});
  });

  // [PMPERF-SLIDE 2026-10-08] Off-screen load done → PerformancePage slides this team in.
  const settled = !loading && (!!error || (!!data && !!leaderboard));
  useEffect(() => {
    if (settled && onReady) onReady();
  }, [settled, onReady]);
  // Figures drawn while this team waits off-screen arrive by the slide, so no fade-in on top of it.
  // The next load here (a month change, a retry) fades in as before.
  const [quietReveal, setQuietReveal] = useState(false);
  if (onReady && !quietReveal && !loading && data) setQuietReveal(true);
  if (quietReveal && loading) setQuietReveal(false);

  // Shared bar scale: the largest of every PM's inventory/in-bank, so bar lengths
  // are comparable across the leaderboard and a bar "breaks" exactly when that
  // PM's in-bank exceeds their inventory.
  const lbScale = useMemo(
    () => Math.max(1, ...(leaderboard?.rows.flatMap((r) => [r.placedInventory, r.placedInBank]) ?? [])),
    [leaderboard]
  );

  /* [UPSELL-HEALTH 2026-08-24] The three upsell columns are the only HubSpot-sourced figures
     in this table. When that fetch fails the server sums them to 0, which reads as "nobody
     upsold this month" — so render an em-dash instead. `!== false` keeps a payload from an
     older server (no flag) reading as healthy. */
  const upsellOk = data?.upsellOk !== false;
  const upMoney = (v: number) => (upsellOk ? fmtMoney(v) : '—');

  // Expected Retention = (Retained + Expected + At-Risk Expected) / Placed Inventory — i.e.
  // the sum of the three displayed status columns over the placed recurring inventory.
  const expectedRetentionPct = (r: TeamPerformanceRow) =>
    r.effectiveInventory > 0
      ? ((r.retained + r.expected + r.atRiskExpected) / r.effectiveInventory) * 100
      : 0;

  const cells = (r: TeamPerformanceRow) => (
    <>
      <Td>{r.pm}</Td>
      <Td>{fmtMoney(r.effectiveInventory)}</Td>
      {/* [RETAINED-RETENTION 2026-07-10] Retained column shows SUM(retention) of Retained
          rows, not the inventory amount. Expected Retention (below) still uses r.retained (amount). */}
      <Td>{fmtMoney(r.retainedRetention)}</Td>
      <Td>{fmtMoney(r.expected)}</Td>
      <Td>{fmtMoney(r.atRisk)}</Td>
      <Td>{fmtMoney(r.atRiskExpected)}</Td>
      <Td>{fmtMoney(r.cancelled)}</Td>
      <Td>{fmtPct(r.retentionPct)}</Td>
      <Td>{fmtPct(expectedRetentionPct(r))}</Td>
      <Td>{upMoney(r.recurringUpsell)}</Td>
      <Td>{upMoney(r.oneTimeUpsell)}</Td>
      <Td>{upMoney(r.totalUpsell)}</Td>
      {/* [PM-PERF-RD 2026-08-25] DB-sourced (refunds/disputes by assignee, the My Inventory KPI
          rules) - not HubSpot, so plain fmtMoney rather than the upsell health em-dash. */}
      <Td>{fmtMoney(r.refundsDisputes)}</Td>
      <Td>{fmtMoney(r.disputesWon)}</Td>
    </>
  );

  return (
    <Container>
      <Toolbar>
        <PageTitle>{title}</PageTitle>
        <FilterSlot>
          <GlassSelect liquid /* [LIQUID-GLASS 2026-11-02] */ deferChange /* [FLUID-8 2026-10-12] instant pick */ value={month} onChange={(e) => setMonth(e.target.value)}>
            {months.length === 0 && <option value={month}>{formatMonthFull(month)}</option>}
            {(months.length > 0 && !months.includes(month) ? [month, ...months] : months).map((m) => (
              <option key={m} value={m}>{formatMonthFull(m)}</option>
            ))}
          </GlassSelect>
        </FilterSlot>
      </Toolbar>

      {error ? (
        // [LOAD-STATES 2026-09-29, user] Was the raw server text ("Internal server error").
        forbidden ? <EmptyState>{error}</EmptyState> : <LoadFailed what="performance figures" onRetry={() => setReloadN((n) => n + 1)} />
      ) : loading || !data ? (
        // Any load that flips `loading` on — first mount, team switch (clears data
        // above) OR a month-dropdown refetch — shows the full skeleton for the newly
        // selected data, rather than dimming the previous month's report. Realtime
        // silent refreshes never set `loading`, so they update in place with no flash.
        <PerfSkeleton />
      ) : (
        <Reveal $plain $rows={!quietReveal} style={quietReveal ? { animation: 'none' } : undefined}>{/* [PMPERF-SLIDE 2026-10-08] quietReveal · [FLUID-6 2026-10-12] the rows rise in when the team's report lands (a team that loads after it slides in, a tab visit) */}
          {/* [MOTION-A 2026-08-20] Fades the real content in as it replaces the skeleton. */}
        <div>
          <Section>
            {/* [PMPERF-SLIDE 2026-10-08] data-slide-item: the tiles cascade in when a team slides in (ViewSlide). */}
            <KpiRow>
              <KpiCard data-slide-item>
                <KpiLabel>Placed Inventory</KpiLabel>
                <KpiValue>{fmtMoney(data.kpis.totalInventory)}</KpiValue>
              </KpiCard>
              <KpiCard data-slide-item>
                <KpiLabel>Placed In Bank</KpiLabel>
                <KpiValue>{fmtMoney(data.kpis.totalPlaced)}</KpiValue>
              </KpiCard>
              <KpiCard data-slide-item>
                <KpiLabel>Expected Retention</KpiLabel>
                <KpiValue>{fmtPct(data.kpis.expectedRetentionPct)}</KpiValue>
              </KpiCard>
              <KpiCard data-slide-item>
                <KpiLabel>Current Retention</KpiLabel>
                <KpiValue>{fmtPct(data.kpis.currentRetentionPct)}</KpiValue>
              </KpiCard>
            </KpiRow>
          </Section>

          {/* ── Retention funnel: where this month's inventory sits ── */}
          <Section>
            <Panel>
              {/* [RESPONSIVE 2026-06-23] wrap so the title + amount stack on very
                  narrow screens instead of forcing the analytics panel to scroll. */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, gap: 12, flexWrap: 'wrap' }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 700, flexWrap: 'wrap' }}>
                  <span style={{ width: 3, height: 15, borderRadius: 2, background: accentGreen, display: 'inline-block' }} />
                  <span style={{ color: accentGreen }}>Retention Funnel</span>
                  <span style={{ color: theme.muted, fontWeight: 400, opacity: 0.55 }}>|</span>
                  <span style={{ color: theme.text }}>Placed Inventory</span>
                  <span style={{ color: theme.muted, fontWeight: 400, opacity: 0.55 }}>|</span>
                  <span style={{ color: theme.muted, fontWeight: 600 }}>Breakdown</span>
                </span>
                <strong style={{ color: theme.text, fontSize: 15, fontWeight: 800 }}>{fmtMoney(data.funnel.initialExpected)}</strong>
              </div>
              <FunnelView f={data.funnel} />
            </Panel>
          </Section>

          <Section>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
              <SectionHeading>PM Performance</SectionHeading>
              {canDownloadReport && (
                <DownloadReportBtn onClick={openReport}>⬇ Download Report</DownloadReportBtn>
              )}
            </div>
            <TableWrapper>
              <PerfTable style={{ minWidth: 1220 }}>{/* [PM-PERF-RD 2026-08-25] was 1040, +2 columns */}
                <thead>
                  <tr>
                    <Th>Name</Th>
                    <Th>Placed Inventory</Th>
                    <Th>Retained</Th>
                    <Th>Expected</Th>
                    <Th>At-Risk</Th>
                    <Th>At-Risk Expected</Th>
                    <Th>Cancelled</Th>
                    <Th>Current Retention</Th>
                    <Th>Expected Retention</Th>
                    <Th>Recurring Upsell</Th>
                    <Th>One-Time Upsell</Th>
                    <Th>Total Upsell</Th>
                    <Th>Refund/Dispute</Th>
                    <Th>Dispute Won</Th>
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((r) => <tr key={r.pm}>{cells(r)}</tr>)}
                  <TotalRow>{cells(data.totals)}</TotalRow>
                </tbody>
              </PerfTable>
            </TableWrapper>
          </Section>

          {/* [FLUID-3 2026-10-12] Mounted while reportPres holds it — through its close too; inert while closing. */}
          {reportPres.mounted && createPortal(
            <ReportOverlay onClick={() => !downloadingReport && setReportOpen(false)} $open={reportOpen && reportPres.entered} inert={!reportOpen}>
              <ReportPanel onClick={(e) => e.stopPropagation()} $open={reportOpen && reportPres.entered}>
                <div style={{ fontSize: 16, fontWeight: 800, marginBottom: 3 }}>Download PM Performance Report</div>
                <div style={{ fontSize: 12.5, color: theme.muted, marginBottom: 14 }}>Pick the month(s), then choose the PM(s) to include</div>
                <ReportColsBar>
                  <span style={{ fontSize: 11, fontWeight: 700, color: theme.text, letterSpacing: 0.2 }}>Report columns</span>
                  {PM_REPORT_COLUMNS.map((c) => <ReportColChip key={c}>{c}</ReportColChip>)}
                </ReportColsBar>
                <ReportGrid>
                  <div>
                    <ReportColHead>
                      <span>1 · Select month(s){reportMonths.length ? ` · ${reportMonths.length}` : ''}</span>
                    </ReportColHead>
                    {/* [YEAR-COLLAPSE 2026-07-30] Grouped by YEAR and collapsible. The list
                        now spans every month with inventory (29 back to Apr-2024), which is
                        far too long flat. Newest year opens by default, plus any year that
                        already has a pick; selections survive collapsing, so choosing months
                        from several years and downloading one file works. */}
                    <ReportMonthList>
                      {reportMonthGroups.map((g) => {
                        const picked = g.months.filter((m) => reportMonths.includes(m)).length;
                        const allOn = picked === g.months.length && picked > 0;
                        const isOpen = openReportYears.includes(g.year);
                        return (
                          <React.Fragment key={g.year}>
                            <ReportYearRow>
                              <button type="button" className="yr" aria-expanded={isOpen} onClick={() => toggleReportYear(g.year)}>
                                <i className={`fas fa-chevron-${isOpen ? 'down' : 'right'}`} aria-hidden="true" />
                                {g.year}
                                <span className="cnt">{picked ? `${picked} of ${g.months.length}` : `${g.months.length} months`}</span>
                              </button>
                              <button
                                type="button"
                                className="all"
                                onClick={() => setReportMonths((prev) => (allOn
                                  ? prev.filter((m) => !g.months.includes(m))
                                  : [...prev, ...g.months.filter((m) => !prev.includes(m))]))}
                              >
                                {allOn ? 'Clear' : 'All'}
                              </button>
                            </ReportYearRow>
                            {/* [FLUID-3 2026-10-12] The open year's rows ripple in — see ReportYearMonths. */}
                            {isOpen && (
                              <ReportYearMonths>
                                {g.months.map((m) => {
                                  const on = reportMonths.includes(m);
                                  return (
                                    <ReportMonthRow key={m} $on={on}>
                                      <input type="checkbox" checked={on} onChange={() => toggleReportMonth(m)} />
                                      {formatMonthFull(m)}
                                    </ReportMonthRow>
                                  );
                                })}
                              </ReportYearMonths>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </ReportMonthList>
                  </div>
                  <div>
                    <ReportColHead>
                      <span>2 · Select PM(s){availablePms.length ? ` · ${reportPms.length}/${availablePms.length}` : ''}</span>
                      {availablePms.length > 0 && (
                        <ReportColLink onClick={() => setReportPms(allPmsSelected ? [] : availablePms)}>
                          {allPmsSelected ? 'Clear' : 'Select all'}
                        </ReportColLink>
                      )}
                    </ReportColHead>
                    <ReportMonthList>
                      {reportMonths.length === 0 ? <ReportHint>Select month(s) first.</ReportHint>
                        : loadingPms ? <ReportHint>Loading PMs…</ReportHint>
                        : availablePms.length === 0 ? <ReportHint>No PMs with inventory in the selected month(s).</ReportHint>
                        : availablePms.map((pm) => {
                            const on = reportPms.includes(pm);
                            return (
                              <ReportMonthRow key={pm} $on={on}>
                                <input type="checkbox" checked={on} onChange={() => toggleReportPm(pm)} />
                                {pm}
                              </ReportMonthRow>
                            );
                          })}
                    </ReportMonthList>
                  </div>
                </ReportGrid>
                <ReportActions>
                  <GlassBtn onClick={() => setReportOpen(false)} disabled={downloadingReport}>Cancel</GlassBtn>
                  <GlassBtn $primary onClick={handleDownloadReport} disabled={downloadingReport || loadingPms || !reportData || reportMonths.length === 0 || reportPms.length === 0}>
                    {downloadingReport ? 'Generating…' : 'Download'}
                  </GlassBtn>
                </ReportActions>
              </ReportPanel>
            </ReportOverlay>,
            document.body,
          )}

          {/* ── The same funnel, broken out per PM ── */}
          <Section>
            <SectionHeading>Retention Funnel by PM — {formatMonthLabel(data.month)}</SectionHeading>
            <PmFunnelStack>
              {[...data.rows]
                .filter((r) => r.effectiveInventory > 0)
                .sort((a, b) => a.pm.localeCompare(b.pm, undefined, { sensitivity: 'base' }))
                .map((r) => (
                  <Panel key={r.pm} data-slide-item>{/* [PMPERF-SLIDE 2026-10-08] cascades in on a team slide */}
                    <PmFunnelHead>
                      <span className="name">{r.pm}</span>
                      <span className="inv">Placed Inventory<strong>{fmtMoney(r.effectiveInventory)}</strong></span>
                    </PmFunnelHead>
                    <FunnelView f={rowToFunnel(r)} />
                  </Panel>
                ))}
              {data.rows.every((r) => r.effectiveInventory <= 0) && (
                <EmptyNote style={{ gridColumn: '1 / -1' }}>No PM inventory for this month.</EmptyNote>
              )}
            </PmFunnelStack>
          </Section>

          {/* ── PM leaderboard — cumulative rolling window, ranked by In-Bank.
                Independent of the toolbar month filter (own fetch, keyed on team). ── */}
          <Section>
            <SectionHeading>
              PM Leaderboard{leaderboard ? ` — ${leaderboardWindowLabel(leaderboard.start, leaderboard.end)}` : ''}
            </SectionHeading>
            {!leaderboard ? (
              <TableSkeleton
                columns={[
                  { label: 'Rank', align: 'center', headerAlign: 'center' },
                  { label: 'PM', align: 'center', headerAlign: 'center' },
                  { label: 'Placed Inventory', align: 'center', headerAlign: 'center' },
                  { label: 'Net In Bank', align: 'center', headerAlign: 'center' },
                  { label: 'Retention %', align: 'center', headerAlign: 'center' },
                  { label: 'In-Bank vs Inventory', align: 'center', headerAlign: 'center' },
                ]}
                rows={8}
              />
            ) : leaderboard.rows.length === 0 ? (
              <TableWrapper><EmptyNote>No PM inventory in this window.</EmptyNote></TableWrapper>
            ) : (
              <>
                <TableWrapper>
                  <LeaderTable style={{ minWidth: 760 }}>
                    <thead>
                      <tr>
                        <Th>Rank</Th>
                        <Th>PM</Th>
                        <Th>Placed Inventory</Th>
                        <Th>Net In Bank</Th>
                        <Th>Retention %</Th>
                        <Th>In-Bank vs Inventory</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {leaderboard.rows.map((r, i) => (
                        <tr key={r.pm}>
                          <Td><RankBadge $top={i < 3}>{i + 1}</RankBadge></Td>
                          <Td>{r.pm}</Td>
                          <Td>{fmtMoney(r.placedInventory)}</Td>
                          <Td>{fmtMoney(r.placedInBank)}</Td>
                          <Td>{fmtPct(r.retentionPct)}</Td>
                          <Td><BankVsInvBar inventory={r.placedInventory} bank={r.placedInBank} scale={lbScale} /></Td>
                        </tr>
                      ))}
                    </tbody>
                  </LeaderTable>
                </TableWrapper>
                <Legend style={{ justifyContent: 'flex-end' }}>
                  <LegendItem>
                    <Swatch $c={theme.colorScheme === 'dark' ? LB_INV_DARK : LB_INV_LIGHT} /> Placed Inventory
                  </LegendItem>
                  <LegendItem>
                    <Swatch
                      $c={theme.colorScheme === 'dark' ? LB_BANK_DARK : LB_BANK_LIGHT}
                      style={{ boxShadow: `0 0 0 1px ${theme.colorScheme === 'dark' ? 'rgba(7,7,7,0.45)' : 'rgba(22,25,23,0.28)'}` }}
                    /> Net In Bank — breaks past inventory when collected exceeds placed
                  </LegendItem>
                </Legend>
              </>
            )}
          </Section>

          {/* ── Month-over-month trend (team trajectory) ── */}
          <Section>
            <Panel>
              <PanelHeading>Month-over-Month Trend — Last {data.trend.length} Months</PanelHeading>
              <TrendChart points={data.trend} />
              <Legend>
                <LegendItem>
                  <Swatch $c={theme.colorScheme === 'dark' ? 'rgba(14,126,78,0.6)' : 'rgba(20,107,84,0.35)'} />
                  Placed Inventory ($)
                </LegendItem>
                <LegendItem><Swatch $c="rgba(62,175,179,0.9)" /> Placed In Bank ($)</LegendItem>
                <LegendItem><Swatch $c={LINE_COLOR} $line /> Retention %</LegendItem>
              </Legend>
            </Panel>
          </Section>
        </div>
        </Reveal>
      )}
    </Container>
  );
};

export default TeamPerformancePage;
